import { useFormikContext } from 'formik'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { useSessionEntity } from '@context/EntityContext'

import { Body1, Heading6 } from '@ui/Typography'
import { Radio } from '@ui/components/Radio'

import { TickmillCompaniesEnum } from '@utils/companyName.utils'
import { isOne } from '@utils/validations'

import { ClientNciDocumentTypeDto, ClientNciPriorityDto } from '@model/ClientNciPriorityDto'

import { createFormField } from '@global/formField/FormField'
import { createFormRadioField } from '@global/formField/RadioGroupField'

import { FieldWrapper } from '@shared/components/FieldWrapper'

import { DocumentInfoPageFormValuesStep1 } from './DocumentInfoNCIPageStep1Form'

const FormField = createFormField<DocumentInfoPageFormValuesStep1>()
const FormRadioGroupField = createFormRadioField<DocumentInfoPageFormValuesStep1>()

interface DocumentNumberFieldFactoryProps {
  accountNciAvailablePriorities: ClientNciPriorityDto
  handleChooseAnotherDocument?: () => void
}

export const DocumentNumberFieldFactory: React.FC<DocumentNumberFieldFactoryProps> = (props) => {
  const { accountNciAvailablePriorities } = props
  const entity = useSessionEntity()

  return (
    <div className={entity === TickmillCompaniesEnum.TICKMILL_UK ? 'pb-5' : 'pt-5 pb-5'}>
      {isOneDocumentType(accountNciAvailablePriorities) && <DocumentNumberField {...props} />}
      {isMultipleDocumentTypes(accountNciAvailablePriorities) && <DocumentRadioField {...props} />}
    </div>
  )
}

const DocumentNumberField: React.FC<DocumentNumberFieldFactoryProps> = (props) => {
  const { accountNciAvailablePriorities, handleChooseAnotherDocument } = props

  const { t } = useTranslation()

  return (
    <React.Fragment>
      <div className='pb-4'>
        <Heading6 medium>{props.accountNciAvailablePriorities.nciRule.shortName}:</Heading6>
      </div>
      <FormField
        name='nciDocumentNumber'
        label={accountNciAvailablePriorities.nciRule.shortName || ''}
        required
      />
      <div className='pt-2 pb-2'>
        <Body1 isParagraph>
          {t('Sign up.Cant provide')} {accountNciAvailablePriorities.nciRule.shortName}?
        </Body1>
      </div>
      <div className='pt-2 pb-2'>
        <Body1 isParagraph>
          <span onClick={handleChooseAnotherDocument} className='is-link'>
            {t('Sign up.Choose another document')}
          </span>
        </Body1>
      </div>
    </React.Fragment>
  )
}

const DocumentRadioField: React.FC<DocumentNumberFieldFactoryProps> = (props) => {
  const { accountNciAvailablePriorities, handleChooseAnotherDocument } = props
  const { t } = useTranslation()
  const entity = useSessionEntity()

  const { values, setFieldValue, setFieldTouched } =
    useFormikContext<DocumentInfoPageFormValuesStep1>()

  const handleDocument = async (documentType: ClientNciDocumentTypeDto) => {
    await setFieldValue('documentTypeId', documentType.documentTypeId)
    setFieldTouched('documentTypeId')
  }

  return (
    <React.Fragment>
      <FieldWrapper>
        {entity === TickmillCompaniesEnum.TICKMILL_UK && (
          <FormField
            name='nciDocumentNumber'
            label={accountNciAvailablePriorities.nciRule.shortName || ''}
            required
          />
        )}
        <FormRadioGroupField name='documentTypeId' value={values.documentTypeId}>
          {accountNciAvailablePriorities.documentTypes.map((x) => (
            <Radio
              name='documentTypeId'
              value={x.documentTypeId}
              key={x.name}
              onChange={() => handleDocument(x)}
              checked={x.documentTypeId === Number(values.documentTypeId)}
            >
              {x.name}
            </Radio>
          ))}
        </FormRadioGroupField>
        {entity !== TickmillCompaniesEnum.TICKMILL_UK && (
          <FormField
            name='nciDocumentNumber'
            label={accountNciAvailablePriorities.nciRule.shortName || ''}
            required
          />
        )}
      </FieldWrapper>
      {handleChooseAnotherDocument && (
        <div className='pt-2 pb-2'>
          <Body1 isParagraph>
            <span onClick={handleChooseAnotherDocument} className='is-link'>
              {t('Sign up.Choose another document')}
            </span>
          </Body1>
        </div>
      )}
    </React.Fragment>
  )
}

const isOneDocumentType = (value: ClientNciPriorityDto) => {
  return isOne(value.documentTypes.length)
}
const isMultipleDocumentTypes = (value: ClientNciPriorityDto) => {
  return value.documentTypes.length > 1
}
